import { UNKNOWN_CT_BLUE } from '~styles/colors';
import { resolvePaletteColors } from './palette-colors';
import type { KpiPalette } from '../types';

export interface BarValueStep {
    label: string;
    value: number;
    /** Absolute rate 0..1. When present on any step, the list renders in rate mode. */
    rate?: number;
    /** Denominator shown per row as `(base)` in rate mode. */
    base?: number;
}

/** A step whose configured metric was declared unavailable for the window (a
 * null scalar), distinct from a real zero: it carries no value to plot. */
export interface UnavailableBarStep {
    label: string;
    unavailable: true;
}

export type BarStep = BarValueStep | UnavailableBarStep;

/** Narrows a `BarStep` to its value-bearing arm. */
export const hasBarValue = (step: BarStep): step is BarValueStep =>
    !('unavailable' in step);

export type ResolvedBar =
    | (BarValueStep & {
          /** Whole-percent bar width. In rate mode this is `rate*100`; otherwise a share of the largest value. */
          pct: number;
      })
    | UnavailableBarStep;

/** Narrows a `ResolvedBar` to its resolved, value-bearing arm. */
export const isResolvedBarValue = (
    bar: ResolvedBar
): bar is BarValueStep & { pct: number } => !('unavailable' in bar);

/** Fill colours for the `filled` funnel variant, one per bar by position
 * (`resolvePaletteColors`); no palette falls back to CT blue for every bar. */
export const resolveFunnelColors = (
    count: number,
    palette?: KpiPalette
): string[] =>
    resolvePaletteColors(count, palette) ??
    Array<string>(count).fill(UNKNOWN_CT_BLUE);

/**
 * Turn raw bar steps into render-ready bars.
 *
 * Rate mode (any step carries a numeric `rate`): each bar's width is its own
 * absolute rate, `rate*100` rounded to a whole percent and clamped to 0–100 so a
 * rate outside 0..1 never overflows the track, so bars are not relative to one
 * another (e.g. an engagement-drivers-rpp list of independent completion rates).
 *
 * Funnel mode (no step carries a rate): each bar's width is its value as a
 * whole-percent share of the largest value (the top of a funnel = 100%).
 *
 * An unavailable step carries no value or rate, so it is excluded from both the
 * rate-mode check and the max/order computations below, and passes through
 * unresolved (no `pct`); the renderer shows it as a distinct unavailable row.
 *
 * Order, labels and values are preserved either way.
 */
export const resolveBars = (steps: BarStep[]): ResolvedBar[] => {
    const valueSteps = steps.filter(hasBarValue);
    const isRateMode = valueSteps.some((step) => typeof step.rate === 'number');
    const max = valueSteps.reduce((m, step) => Math.max(m, step.value), 0);

    return steps.map((step) => {
        if (!hasBarValue(step)) return step;
        if (isRateMode) {
            return {
                ...step,
                pct: Math.max(
                    0,
                    Math.min(100, Math.round((step.rate ?? 0) * 100))
                ),
            };
        }
        return {
            ...step,
            pct: max > 0 ? Math.round((step.value / max) * 100) : 0,
        };
    });
};
